import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@newcore/ui/buttons';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import { ROUTES } from '@/common/auth-routes';
import { useTenant } from '@/contexts/use-tenant';
import { useApplicationsPageVisible } from '@/hooks/use-applications-page-visible';
import { NAVIGATION_ITEMS, type NavItem } from './navigation-items';

interface MobileNavItemProps {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
}

export const MobileNavigationMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const showApplications = useApplicationsPageVisible();
  const visibleItems = showApplications
    ? NAVIGATION_ITEMS
    : NAVIGATION_ITEMS.filter(item => item.route !== ROUTES.DASHBOARD);

  const handleNavClick = (route: string) => {
    navigate(`/${tenant}${route}`);
  };

  return (
    <nav
      className='fixed bottom-0 left-0 right-0 z-50 bg-primary border-t border-secondary h-16 safe-area-inset-bottom'
      role='navigation'
      aria-label='Mobile navigation'
    >
      <div className='flex h-full'>
        {visibleItems.map(item => (
          <MobileNavItem
            key={item.id}
            item={item}
            isActive={location.pathname === `/${tenant}${item.route}`}
            onClick={() => handleNavClick(item.route)}
          />
        ))}
      </div>
    </nav>
  );
};

const MobileNavItem = React.memo(
  ({ item, isActive, onClick }: MobileNavItemProps) => {
    const Icon = item.icon;

    return (
      <Button
        asChild
        variant={ButtonVariants.TERTIARY}
        className={cn(
          'shadow-none flex flex-col items-center justify-center flex-1 h-full rounded-none px-2 py-2 gap-1',
          isActive ? 'text-primary' : 'text-tertiary'
        )}
      >
        <button
          type='button'
          onClick={onClick}
          aria-current={isActive ? 'page' : undefined}
          data-testid={`mobile-nav-${item.id}`}
        >
          <Icon className='h-5 w-5' />
          <Text size={TextSizes.XS} weight={FontWeights.MEDIUM}>
            {item.title}
          </Text>
        </button>
      </Button>
    );
  }
);

MobileNavItem.displayName = 'MobileNavItem';
